import { createTheme } from '@material-ui/core/styles'
import tinycolor from 'tinycolor2'
import * as Helpers from '#/helpers'

export const colorSwatches = [
  '#0052CC',
  '#172B4D',
  '#4C9AFF',
  '#00B8D9',
  '#36B37E',
  '#FFC400',
  '#FF5630',
  '#6554C0',
  '#DE350B',
  '#FFFFFF',
  '#DEEBFF',
  '#EAE6FF',
  '#E3FCEF',
  '#FFFAE6',
  '#FFEBE6',
]

export const colorSwatchesLess = [
  '#0052CC',
  '#172B4D',
  '#4C9AFF',
  '#00B8D9',
  '#36B37E',
  '#FFC400',
  '#6554C0',
  '#FFFFFF',
  '#DEEBFF',
  '#EAE6FF',
  '#E3FCEF',
  '#FFFAE6',
  '#FFEBE6',
]

export const multiStyle = {
  container: (styles) => ({
    ...styles,
    width: '100%',
    display: 'inline-block',
    fontSize: '13px',
  }),
  control: (styles) => ({
    ...styles,
    backgroundColor: '#f9f9f9',
    border: 'none',
    '& > div': {
      padding: 5,
    },
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: 'rgba(0, 82, 204, 0.1)',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    paddingLeft: 5,
    color: '#0052CC',
    fontWeight: '500',
    fontSize: '13px',
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    '& svg': {
      fill: 'rgba(0, 82, 204, 0.5) !important',
    },
    ':hover': {
      backgroundColor: 'rgba(0, 82, 204, 0.1)',
    },
  }),
}

export const tabTheme = ({ macro, inc, editingTab, isEditor, isJira }) =>
  createTheme({
    overrides: {
      MuiTabs: {
        root: {
          flex: 1,
          minHeight: 'auto',
          '&::after': {
            content: inc('indicator') ? '""' : '',
            position: 'absolute',
            top: macro.vertical ? 0 : null,
            bottom: macro.vertical ? null : 0,
            right: macro.vertical ? 0 : null,
            width: macro.vertical ? `${macro.indicatorPercent}%` : '100%',
            height: macro.vertical ? '100%' : `${macro.indicatorPercent}%`,
            backgroundColor: macro.indicatorColorBG,
            zIndex: 2,
          },
        },
        flexContainer: {
          height: '100%',
        },
        scrollButtons: {
          zIndex: 5,
          color: macro.tabColor,
          borderStyle: 'solid',
          borderColor: macro.separatorColor,
          '&:first-child': {
            borderWidth: inc('separator')
              ? macro.vertical
                ? `0 0 ${macro.separatorWidth}px 0`
                : `0 ${macro.separatorWidth}px 0 0`
              : 0,
            boxShadow: Helpers.isDark(macro.tabColorBG)
              ? '0px 0px 30px rgba(0, 0, 0, 1)'
              : '0px 0px 30px rgba(0, 0, 0, 0.2)',
            transition: 'all 250ms ease-in-out',
          },
          '&:last-child': {
            borderWidth: inc('separator')
              ? macro.vertical
                ? `${macro.separatorWidth}px 0 0 0`
                : `0 0 0 ${macro.separatorWidth}px`
              : 0,
            boxShadow: Helpers.isDark(macro.tabColorBG)
              ? '0px 0px 30px rgba(0, 0, 0, 1)'
              : '0px 0px 30px rgba(0, 0, 0, 0.2)',
            transition: 'all 250ms ease-in-out',
          },
          '&.Mui-disabled': {
            opacity: 1,
            boxShadow: 'none',
          },
          '&.Mui-disabled > svg': {
            opacity: 0.5,
          },
          '& svg': {
            fontSize: '24px',
          },
          '&:hover': {
            backgroundColor: tinycolor(macro.tabColorBG).isLight()
              ? 'rgba(0, 0, 0, 0.02)'
              : 'rgba(255, 255, 255, 0.02)',
          },
        },
        indicator: {
          display: editingTab ? 'none' : 'block',
          backgroundColor: inc('indicator')
            ? macro.indicatorColor
            : 'transparent',
          width: macro.vertical ? `${macro.indicatorPercent}%` : null,
          height: macro.vertical ? null : `${macro.indicatorPercent}%`,
          zIndex: 3,
        },
      },
      MuiTab: {
        root: {
          height: '100%',
          width: '100%',
          minHeight: 'auto',
          maxWidth: 'auto',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          cursor: isEditor && !isJira ? 'grab' : 'pointer',
          backgroundColor: ({ config, isDragging }) =>
            !isDragging && inc('indicator') && macro.indicatorPercent === 100
              ? 'transparent'
              : inc('indicator') && macro.indicatorPercent === 100
              ? macro.tabColorBG
              : config.tabColorBG || macro.tabColorBG,
          padding: `${macro.padding}px`,
          boxShadow: ({ isDragging }) =>
            isDragging
              ? Helpers.isDark(macro.tabColorBG)
                ? '0px 0px 30px rgba(0, 0, 0, 1)'
                : '0px 0px 30px rgba(0, 0, 0, 0.2)'
              : 'none',
          zIndex:
            inc('indicator') &&
            macro.indicatorColorBG.toLowerCase() !==
              macro.tabColorBG.toLowerCase()
              ? 2
              : 4,
          borderStyle: 'solid',
          borderColor: macro.separatorColor,
          borderWidth: inc('separator')
            ? macro.vertical
              ? `0 0 ${macro.separatorWidth}px 0`
              : `0 ${macro.separatorWidth}px 0 0`
            : 0,
          '&:hover': {
            zIndex:
              inc('hover') && !inc('indicator')
                ? 4
                : inc('indicator') && macro.indicatorPercent === 100
                ? 3
                : 2,
            opacity: '1 !important',
            backgroundColor: inc('hover') ? macro.hoverColorBG : null,
            '& > span': {
              color: inc('hover') ? macro.hoverColor : null,
            },
          },
          '&.MuiTab-fullWidth': {
            width: '100%',
          },
          '&.Mui-selected': {
            zIndex: inc('indicator') && macro.indicatorPercent === 100 ? 4 : 2,
            backgroundColor: ({ isDragging }) =>
              inc('active')
                ? macro.activeColorBG
                : inc('indicator') && macro.indicatorPercent === 100
                ? isDragging
                  ? macro.indicatorColor
                  : 'transparent !important'
                : null,
            '& > span': {
              color: inc('active') ? `${macro.activeColor} !important` : null,
            },
            '&:hover': {
              backgroundColor: null,
            },
          },
          '&.MuiTab-textColorInherit': {
            opacity: macro.opacity / 100,
          },
        },
        wrapper: {
          lineHeight: '24px',
          color: ({ config }) => config.tabColor || macro.tabColor,
          fontSize: `${macro.tabSize}px`,
          fontWeight: ({ config }) =>
            config.tabBold || macro.tabBold ? '600' : 'normal',
          fontStyle: ({ config }) =>
            config.tabItalic || macro.tabItalic ? 'italic' : 'normal',
          textDecoration: ({ config }) =>
            config.tabUnderline || macro.tabUnderline ? 'underline' : 'none',
          flexDirection: inc('icons') && macro.iconInline ? 'row' : 'column',
          textAlign: inc('icons') && macro.iconInline ? 'left' : 'center',
          justifyContent: macro.vertical ? 'flex-start' : 'center',
          textTransform: 'initial',
          fontFamily: Helpers.fonts.filter(
            (x) => x.label === (macro?.tabFont || 'Atlassian')
          )[0].value,
          '& > span': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '0 !important',
            marginRight:
              macro.iconInline && inc('titles') ? '5px' : '0 !important',
          },
        },
        labelIcon: {
          minHeight: 'auto',
          paddingTop: `${macro.padding}px`,
        },
      },
    },
  })

export const customButton = (macro, hasIndicator) => ({
  position: 'relative',
  justifyContent: 'center',
  alignItems: 'center',
  width: macro.vertical ? '100%' : 40,
  height: macro.vertical ? 40 : '100%',
  borderRadius: 0,
  backgroundColor: macro.tabColorBG,
  transition: 'none',
  zIndex: hasIndicator && macro.indicatorPercent === 100 ? 5 : 1,
  '&:hover': {
    backgroundColor: tinycolor(macro.tabColorBG).isLight()
      ? 'rgba(0,0,0,0.02)'
      : 'rgba(255,255,255,0.02)',
    zIndex: 2,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: tinycolor(macro.tabColorBG).isLight()
      ? 'rgba(0,0,0,0.02)'
      : 'rgba(255,255,255,0.02)',
  },
})
